"use client";

import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { EyebrowBadge, GooglePlayBadge, HeroGlow } from "@/components/ui/";
import {
  Zap,
  Shield,
  Wifi,
  Layers,
  LayoutDashboard,
  Activity,
  Gauge,
  Unlock,
} from "@/components/icons";
import { PLAY_STORE_URL } from "@/lib/constants";
import { useUptime } from "@/hooks/use-uptime";

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.05 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const features = [
  { icon: Zap, text: "Create a 4G proxy, LTE proxy, or 5G proxy in minutes" },
  { icon: Gauge, text: "Ultra-fast mobile proxies using proprietary technology" },
  { icon: Shield, text: "Supports HTTP(s) and SOCKSv5 proxies" },
  { icon: Layers, text: "Supports dual-stacking IPV4/IPV6" },
  { icon: LayoutDashboard, text: "Manage all devices from a web interface" },
  { icon: Activity, text: "UPTIME_PLACEHOLDER" },
  { icon: Wifi, text: "High speed" },
  { icon: Unlock, text: "No root required" },
];

function PhoneMockup() {
  return (
    <div className="relative flex items-center justify-center">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute top-1/2 left-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-green-400/10 blur-3xl" />
      </div>

      <div className="relative w-[260px] sm:w-[280px]">
        <div
          className="relative rounded-[44px] p-[3px]"
          style={{
            background:
              "linear-gradient(160deg, color-mix(in srgb, var(--foreground) 18%, transparent) 0%, color-mix(in srgb, var(--foreground) 4%, transparent) 60%, color-mix(in srgb, var(--foreground) 10%, transparent) 100%)",
          }}
        >
          <div
            className="relative overflow-hidden rounded-[42px]"
            style={{
              backgroundColor: "#141416",
              boxShadow: "0 40px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.05) inset",
            }}
          >
            <div className="flex justify-center pt-4 pb-1">
              <div className="h-7 w-24 rounded-full" style={{ backgroundColor: "#000" }} />
            </div>

            <div
              className="mx-2 mb-2 flex flex-col items-center justify-center gap-3 overflow-hidden rounded-[32px]"
              style={{
                height: 480,
                background: "linear-gradient(160deg, #0F1117 0%, #12141C 50%, #0A0C14 100%)",
                border: "1px solid rgba(255,255,255,0.05)",
              }}
              aria-label="App screenshot placeholder"
            >
              <div className="flex flex-col items-center gap-3 px-6 text-center opacity-40">
                <div
                  className="flex size-16 items-center justify-center rounded-2xl"
                  style={{
                    background: "rgba(74,222,128,0.15)",
                    border: "1px solid rgba(74,222,128,0.2)",
                  }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="size-8 text-green-400"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <rect x="5" y="2" width="14" height="20" rx="3" />
                    <circle cx="12" cy="18" r="1" />
                  </svg>
                </div>
                <p className="text-xs leading-relaxed text-[#a0a0a0]">
                  App screenshot
                  <br />
                  coming soon
                </p>
              </div>
            </div>
          </div>
        </div>

        <div
          aria-hidden
          className="bg-foreground/12 absolute top-24 -right-[3px] h-8 w-[3px] rounded-l-full"
        />
        <div
          aria-hidden
          className="bg-foreground/12 absolute top-36 -right-[3px] h-8 w-[3px] rounded-l-full"
        />
        <div
          aria-hidden
          className="bg-foreground/12 absolute top-28 -left-[3px] h-12 w-[3px] rounded-r-full"
        />
      </div>
    </div>
  );
}

export default function AndroidSection() {
  const prefersReducedMotion = useReducedMotion();
  const uptime = useUptime();

  return (
    <section
      className="relative overflow-y-clip pt-32 pb-28 lg:pt-40 lg:pb-36"
      aria-labelledby="android-heading"
    >
      <HeroGlow align="center" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={prefersReducedMotion ? "visible" : undefined}
            whileInView="visible"
            viewport={{ ...scrollRevealViewport, amount: 0.05 }}
            className="flex flex-col"
          >
            <motion.div variants={itemVariants}>
              <EyebrowBadge>Android App</EyebrowBadge>
            </motion.div>

            <motion.h1
              id="android-heading"
              variants={itemVariants}
              className="text-foreground mb-5 text-4xl leading-[1.1] font-bold tracking-tight sm:text-5xl lg:text-6xl"
            >
              Proxidize Android App
            </motion.h1>

            <motion.p
              variants={itemVariants}
              className="text-muted-foreground mb-8 max-w-lg text-base leading-relaxed"
            >
              Manage your proxy network from anywhere from the Proxidize Cloud Dashboard. Proxidize
              Android Agent lets you turn your phone into a 4G/LTE/5G mobile proxy from a single
              interface.
            </motion.p>

            <motion.div variants={itemVariants} className="mb-10">
              <p className="text-muted-foreground mb-4 text-xs font-semibold">Key Features</p>
              <ul className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                {features.map(({ icon: Icon, text }) => (
                  <li key={text} className="text-foreground/80 flex items-start gap-2.5 text-sm">
                    <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-md bg-green-400/10">
                      <Icon size={12} className="light:text-green-700 text-green-400" aria-hidden />
                    </span>
                    {text === "UPTIME_PLACEHOLDER" ? `${uptime.toFixed(3)}% uptime` : text}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div variants={itemVariants}>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block transition-transform hover:-translate-y-0.5 active:translate-y-0"
                aria-label="Get Proxidize on Google Play"
              >
                <GooglePlayBadge />
              </a>
            </motion.div>
          </motion.div>

          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ ...scrollRevealViewport, amount: 0.05 }}
            transition={{ duration: 0.65, ease: "easeOut" }}
            className="flex justify-center lg:justify-end"
          >
            <PhoneMockup />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
